import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { Company, CompanyFormData } from '../types/company';
import api from '../services/api';
import { format } from 'date-fns';

interface CompaniesProps {
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

const initialFormData: CompanyFormData = {
  name: '',
  brand_name: '',
  email: '',
  contact_number: '',
  address: '',
  is_active: true
};

function getApiErrorMessage(error: any, fallback: string): string {
  return error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || fallback;
}

const Companies: React.FC<CompaniesProps> = ({ canAdd, canEdit, canDelete }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<CompanyFormData>(initialFormData);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userFormData, setUserFormData] = useState({
    email: '',
    name: '',
    role: 'USER',
    password: ''
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to fetch companies');
    }
  };

  const handleOpen = (company?: Company) => {
    if (company) {
      setSelectedCompany(company);
      setFormData({
        name: company.name,
        brand_name: company.brand_name,
        email: company.email || '',
        contact_number: company.contact_number || '',
        address: company.address || '',
        is_active: company.is_active
      });
    } else {
      setSelectedCompany(null);
      setFormData(initialFormData);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setError('');
    setSuccess('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'is_active' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (!formData.name || !formData.brand_name) {
        setError('Company name and brand name are required');
        return;
      }

      if (selectedCompany) {
        await api.put(`/companies/${selectedCompany.id}`, formData);
        setSuccess('Company updated successfully');
      } else {
        await api.post('/companies', formData);
        setSuccess('Company created successfully');
      }

      fetchCompanies();
      handleClose();
    } catch (error: any) {
      setError(getApiErrorMessage(error, 'Failed to save company'));
    }
  };

  const handleDeleteCompany = async (companyId: number) => {
    if (!window.confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/companies/${companyId}`);
      setSuccess('Company deleted successfully');
      fetchCompanies();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to delete company');
    }
  };

  const handleCloseUserDialog = () => {
    setUserDialogOpen(false);
    setError('');
  };

  const handleUserFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedCompany) {
      setError('No company selected');
      return;
    }

    try {
      await api.post(`/companies/${selectedCompany.id}/users`, userFormData);
      setSuccess('User created successfully');
      fetchCompanies();
      handleCloseUserDialog();
    } catch (error: any) {
      setError(getApiErrorMessage(error, 'Failed to create user'));
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Companies
        </Typography>
        {canAdd && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Add Company
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Brand Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created At</TableCell>
              {(canEdit || canDelete) && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map((company) => (
              <TableRow key={company.id}>
                <TableCell>{company.name}</TableCell>
                <TableCell>{company.brand_name}</TableCell>
                <TableCell>{company.email}</TableCell>
                <TableCell>{company.contact_number}</TableCell>
                <TableCell>
                  <Chip
                    label={company.is_active ? 'Active' : 'Inactive'}
                    color={company.is_active ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {format(new Date(company.created_at), 'dd/MM/yyyy')}
                </TableCell>
                {(canEdit || canDelete) && (
                  <TableCell>
                    {canEdit && (
                      <IconButton onClick={() => handleOpen(company)} size="small">
                        <EditIcon />
                      </IconButton>
                    )}
                    {canDelete && (
                      <IconButton
                        onClick={() => handleDeleteCompany(company.id)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {selectedCompany ? 'Edit Company' : 'Add Company'}
          </DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              name="name"
              label="Company Name"
              fullWidth
              value={formData.name}
              onChange={handleChange}
              required
            />
            <TextField
              margin="dense"
              name="brand_name"
              label="Brand Name"
              fullWidth
              value={formData.brand_name}
              onChange={handleChange}
              required
            />
            <TextField
              margin="dense"
              name="email"
              label="Email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              name="contact_number"
              label="Contact Number"
              fullWidth
              value={formData.contact_number}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              name="address"
              label="Address"
              fullWidth
              multiline
              rows={3}
              value={formData.address}
              onChange={handleChange}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleChange}
                  name="is_active"
                />
              }
              label="Active"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {selectedCompany ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={userDialogOpen} onClose={handleCloseUserDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleCreateUser}>
          <DialogTitle>Add User</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              name="name"
              label="Name"
              fullWidth
              value={userFormData.name}
              onChange={handleUserFormChange}
              required
            />
            <TextField
              margin="dense"
              name="email"
              label="Email"
              type="email"
              fullWidth
              value={userFormData.email}
              onChange={handleUserFormChange}
              required
            />
            <TextField
              margin="dense"
              name="password"
              label="Password"
              type="password"
              fullWidth
              value={userFormData.password}
              onChange={handleUserFormChange}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUserDialog}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Create User
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Companies; 
